// Unique content for each CareFirst home health service (Columbus, OH)
import { IMAGES } from "../mock";

export const SERVICES = [
  {
    slug: "skilled-nursing",
    name: "Skilled Nursing",
    icon: "stethoscope",
    image: IMAGES.nurseBP,
    short: "Clinical nursing care at home from licensed RNs and LPNs.",
    seoTitle: "Skilled Nursing at Home in Columbus, OH | CareFirst Home Health",
    seoDesc:
      "Licensed skilled nursing services at home in Columbus, Ohio. Wound care, medication administration, disease management and post-hospital recovery from CareFirst Home Health.",
    intro:
      "Our licensed nurses bring hospital-quality clinical care into the comfort of your home. Skilled nursing supports recovery, manages complex conditions, and keeps patients safely out of the hospital.",
    whoQualifies: [
      "Patients recovering from surgery, illness, or a hospital stay",
      "Individuals with wounds, IV therapy, or injections",
      "People managing chronic or complex medical conditions",
      "Patients needing ongoing monitoring ordered by a physician",
    ],
    benefits: [
      "Care delivered by licensed RNs and LPNs",
      "Reduced hospital readmissions",
      "Coordination with your physician and family",
      "Personalized nursing care plans",
    ],
    reasons: [
      "Wound and ostomy care",
      "Medication administration and injections",
      "Post-surgical monitoring",
      "Disease and symptom management",
    ],
    expectations:
      "A nurse completes an assessment, coordinates with your physician, and builds a care plan with scheduled visits, clear communication, and progress updates for the family.",
  },
  {
    slug: "home-health-aide",
    name: "Home Health Aide",
    icon: "handHeart",
    image: IMAGES.dressing,
    short: "Certified aides assisting with daily living activities.",
    seoTitle: "Home Health Aide in Columbus, OH | CareFirst Home Health",
    seoDesc:
      "Certified home health aides in Columbus, Ohio provide bathing, grooming, mobility and daily living support so loved ones stay safe and independent at home.",
    intro:
      "Our trained home health aides provide dependable, hands-on help with the everyday activities that keep clients comfortable, clean, and confident at home.",
    whoQualifies: [
      "Seniors needing help with activities of daily living",
      "Adults with disabilities or limited mobility",
      "Patients transitioning home after a hospital stay",
      "Anyone needing reliable personal support at home",
    ],
    benefits: [
      "Certified, background-checked aides",
      "Support with bathing, dressing, and mobility",
      "Encourages dignity and independence",
      "Flexible scheduling that fits your routine",
    ],
    reasons: [
      "Bathing and grooming assistance",
      "Help with dressing and toileting",
      "Mobility and transfer support",
      "Light assistance around the home",
    ],
    expectations:
      "We match each client with a compatible aide, create a routine you approve, and stay in close contact with the family to adjust care as needs change.",
  },
  {
    slug: "personal-care",
    name: "Personal Care",
    icon: "sparkles",
    image: IMAGES.grooming,
    short: "Compassionate help with bathing, dressing, and grooming.",
    seoTitle: "Personal Care Services in Columbus, OH | CareFirst Home Health",
    seoDesc:
      "Personal care services in Columbus, Ohio. Compassionate help with bathing, dressing, grooming and hygiene delivered with dignity by CareFirst caregivers.",
    intro:
      "Personal care preserves dignity and comfort by assisting with the private, daily tasks that become harder with age, illness, or disability.",
    whoQualifies: [
      "Seniors who need help with hygiene and grooming",
      "Individuals recovering from illness or injury",
      "Adults with disabilities needing daily assistance",
      "Medicaid, PASSPORT, and waiver program participants",
    ],
    benefits: [
      "Respectful, one-on-one assistance",
      "Improved comfort, safety, and hygiene",
      "Consistent, familiar caregivers",
      "Peace of mind for families",
    ],
    reasons: [
      "Assistance with bathing and showering",
      "Dressing and grooming support",
      "Incontinence and toileting care",
      "Skin and hygiene maintenance",
    ],
    expectations:
      "A caregiver provides gentle, respectful assistance on a schedule that works for you, always focused on comfort, privacy, and independence.",
  },
  {
    slug: "homemaker-services",
    name: "Homemaker Services",
    icon: "home",
    image: IMAGES.meal,
    short: "Light housekeeping, meals, and everyday home support.",
    seoTitle: "Homemaker Services in Columbus, OH | CareFirst Home Health",
    seoDesc:
      "Homemaker services in Columbus, Ohio: light housekeeping, meal preparation, laundry and errands that keep home safe and comfortable for seniors.",
    intro:
      "Homemaker services keep the home clean, safe, and comfortable so clients can focus on living well instead of daily chores.",
    whoQualifies: [
      "Seniors living independently who need light help",
      "Individuals recovering at home",
      "Waiver and Medicaid program participants",
      "Families needing extra household support",
    ],
    benefits: [
      "A cleaner, safer living environment",
      "Nutritious, home-cooked meals",
      "Reduced fall and safety risks",
      "More time for what matters most",
    ],
    reasons: [
      "Light housekeeping and laundry",
      "Meal planning and preparation",
      "Grocery shopping and errands",
      "Organizing a safe living space",
    ],
    expectations:
      "Your caregiver handles agreed-upon household tasks on a regular schedule, keeping the home tidy, stocked, and safe.",
  },
  {
    slug: "respite-care",
    name: "Respite Care",
    icon: "clock",
    image: IMAGES.heroCaregiver,
    short: "Short-term relief for family caregivers.",
    seoTitle: "Respite Care in Columbus, OH | CareFirst Home Health",
    seoDesc:
      "Respite care in Columbus, Ohio gives family caregivers a trusted break. Short-term, flexible in-home relief care from CareFirst Home Health.",
    intro:
      "Respite care gives family caregivers a much-needed break while ensuring their loved one continues to receive safe, attentive care at home.",
    whoQualifies: [
      "Family caregivers needing time to rest or travel",
      "Households caring for a loved one full time",
      "Caregivers managing work and care responsibilities",
      "Families needing occasional or scheduled relief",
    ],
    benefits: [
      "Prevents caregiver burnout",
      "Flexible hourly or extended coverage",
      "Continuity of your loved one's routine",
      "Reliable, compassionate substitute care",
    ],
    reasons: [
      "Caregiver rest and recovery",
      "Travel or work obligations",
      "Appointments and personal time",
      "Planned or emergency coverage",
    ],
    expectations:
      "We learn your loved one's routine and preferences, then step in seamlessly so you can take time for yourself with complete peace of mind.",
  },
  {
    slug: "medication-management",
    name: "Medication Management",
    icon: "pill",
    image: IMAGES.medication,
    short: "Reminders and oversight for safe, on-time medications.",
    seoTitle: "Medication Management at Home in Columbus, OH | CareFirst",
    seoDesc:
      "In-home medication management in Columbus, Ohio. Reminders, organization and nurse oversight to help patients take the right medications at the right time.",
    intro:
      "Medication management helps patients take the right medications, in the right dose, at the right time — reducing errors and supporting better health.",
    whoQualifies: [
      "Seniors managing multiple prescriptions",
      "Patients recently discharged from the hospital",
      "Individuals with memory or cognitive changes",
      "Anyone at risk of missed or double doses",
    ],
    benefits: [
      "Fewer medication errors",
      "Better management of chronic conditions",
      "Nurse oversight and coordination",
      "Reassurance for families",
    ],
    reasons: [
      "Complex or multiple medications",
      "Frequent prescription changes",
      "Memory or vision challenges",
      "Recent hospital discharge",
    ],
    expectations:
      "Our team organizes medications, provides reminders or administration as ordered, and monitors for side effects while coordinating with your provider.",
  },
  {
    slug: "chronic-disease-support",
    name: "Chronic Disease Support",
    icon: "activity",
    image: IMAGES.nurseSmiling,
    short: "Ongoing help managing long-term health conditions.",
    seoTitle: "Chronic Disease Support at Home in Columbus, OH | CareFirst",
    seoDesc:
      "Chronic disease support in Columbus, Ohio for diabetes, COPD, heart disease and more. Home monitoring and education to help patients stay stable and independent.",
    intro:
      "Living with a chronic condition is easier with the right support. We help patients manage symptoms, follow care plans, and stay independent at home.",
    whoQualifies: [
      "Patients with diabetes, COPD, or heart disease",
      "Individuals with high blood pressure or kidney disease",
      "Patients needing ongoing monitoring and education",
      "Anyone managing a long-term health condition",
    ],
    benefits: [
      "Symptom monitoring and early intervention",
      "Education for patients and families",
      "Fewer emergencies and hospital visits",
      "Support following physician care plans",
    ],
    reasons: [
      "Diabetes and blood sugar management",
      "COPD and respiratory support",
      "Heart-health monitoring",
      "Education and lifestyle coaching",
    ],
    expectations:
      "We create a condition-specific plan, monitor key health indicators, teach self-management skills, and keep your physician informed.",
  },
  {
    slug: "post-hospitalization-care",
    name: "Post-Hospitalization Care",
    icon: "heartPulse",
    image: IMAGES.recoveryWalker,
    short: "Safe recovery support in the days after discharge.",
    seoTitle: "Post-Hospital Home Care in Columbus, OH | CareFirst Home Health",
    seoDesc:
      "Post-hospitalization home care in Columbus, Ohio. Recovery support, medication help and monitoring after discharge to reduce readmissions and speed healing.",
    intro:
      "The days after a hospital stay are critical. Our transitional care helps patients recover safely at home and lowers the risk of readmission.",
    whoQualifies: [
      "Patients recently discharged from a hospital",
      "Individuals recovering from surgery",
      "Patients with new medications or care instructions",
      "Anyone needing extra support during recovery",
    ],
    benefits: [
      "Smoother transition from hospital to home",
      "Lower risk of readmission",
      "Help understanding discharge instructions",
      "Coordinated follow-up care",
    ],
    reasons: [
      "Recovery from surgery or illness",
      "New or changed medications",
      "Follow-up appointment coordination",
      "Monitoring for complications",
    ],
    expectations:
      "We review discharge orders, arrange care quickly, monitor recovery, and coordinate with your care team to keep healing on track.",
  },
  {
    slug: "companion-care",
    name: "Companion Care",
    icon: "users",
    image: IMAGES.eligibility,
    short: "Friendly companionship and everyday engagement.",
    seoTitle: "Companion Care in Columbus, OH | CareFirst Home Health",
    seoDesc:
      "Companion care in Columbus, Ohio provides friendly company, conversation, activities and errands that reduce isolation and support emotional well-being.",
    intro:
      "Companionship is essential to well-being. Our caregivers provide friendly conversation, engagement, and support that brightens each day.",
    whoQualifies: [
      "Seniors living alone or feeling isolated",
      "Individuals needing social engagement",
      "Clients who need light, non-medical support",
      "Families wanting reassurance and check-ins",
    ],
    benefits: [
      "Reduced loneliness and isolation",
      "Improved mood and mental well-being",
      "Encouragement to stay active and social",
      "Regular check-ins for peace of mind",
    ],
    reasons: [
      "Conversation and companionship",
      "Games, hobbies, and activities",
      "Accompaniment to outings and errands",
      "Wellness check-ins",
    ],
    expectations:
      "A consistent companion builds a genuine relationship, plans enjoyable activities, and keeps family updated on well-being.",
  },
  {
    slug: "private-duty-nursing",
    name: "Private Duty Nursing",
    icon: "shield",
    image: IMAGES.nurseSmiling,
    short: "Extended one-on-one nursing for complex needs.",
    seoTitle: "Private Duty Nursing in Columbus, OH | CareFirst Home Health",
    seoDesc:
      "Private duty nursing in Columbus, Ohio provides extended, one-on-one skilled nursing at home for individuals with complex or long-term medical needs.",
    intro:
      "Private duty nursing provides continuous, one-on-one skilled care for individuals with complex or long-term medical needs — hourly, overnight, or around the clock.",
    whoQualifies: [
      "Individuals needing extended hourly nursing",
      "Patients with complex or long-term conditions",
      "DODD and waiver program participants",
      "Families needing overnight or 24/7 care",
    ],
    benefits: [
      "Dedicated one-on-one nursing",
      "Continuity with familiar nurses",
      "Flexible hourly to 24/7 coverage",
      "Close coordination with providers",
    ],
    reasons: [
      "Ventilator or tracheostomy care",
      "Complex medication regimens",
      "Ongoing monitoring needs",
      "Extended or overnight nursing",
    ],
    expectations:
      "We build a dedicated nursing schedule, assign consistent nurses, and coordinate closely with your medical team for safe, continuous care.",
  },
];

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);
